import { useMemo, useState } from "react";
import { api } from "./api";
import "./activity-history.css";

export type ActivityRecord = {
  id: string;
  occurred_at: string;
  activity_type: string | null;
  title: string;
  duration_minutes: number | null;
  calories_burned_kcal: number | null;
  intensity: string | null;
  notes: string | null;
  source: "manual" | "gpt" | "import";
  created_at: string;
};

type Props = {
  activities: ActivityRecord[];
  onDeleted: (id: string) => void;
  onMessage: (message: string) => void;
  onError: (message: string) => void;
};

function dateValue(value: string): number {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? 0 : date.getTime();
}

function formatDate(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat("ja-JP", {
    timeZone: "Asia/Tokyo",
    month: "numeric",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}

function sourceLabel(source: ActivityRecord["source"]): string {
  if (source === "gpt") return "GPT";
  if (source === "import") return "取込";
  return "手動";
}

function intensityLabel(value: string | null): string {
  if (!value) return "強度 —";
  const labels: Record<string, string> = {
    light: "軽め",
    moderate: "普通",
    vigorous: "高め",
    low: "軽め",
    medium: "普通",
    high: "高め",
  };
  return `強度 ${labels[value] ?? value}`;
}

export function ActivityHistory({ activities, onDeleted, onMessage, onError }: Props) {
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const sorted = useMemo(
    () => [...activities].sort((a, b) => dateValue(b.occurred_at) - dateValue(a.occurred_at)),
    [activities],
  );

  async function deleteActivity(activity: ActivityRecord) {
    if (!window.confirm(`「${activity.title}」の運動記録を削除しますか？`)) return;
    setDeletingId(activity.id);
    onError("");
    onMessage("");
    try {
      await api<{ ok: true; deleted: true }>(`/api/app/activities/${encodeURIComponent(activity.id)}`, {
        method: "DELETE",
      });
      onDeleted(activity.id);
      onMessage("運動記録を削除しました。");
    } catch (error) {
      onError(error instanceof Error ? error.message : "運動記録を削除できませんでした。");
    } finally {
      setDeletingId(null);
    }
  }

  return (
    <section className="panel activity-history">
      <div className="panel-heading">
        <div><p className="eyebrow">ACTIVITY HISTORY</p><h2>運動履歴</h2></div>
        <span className="progress-count">{sorted.length}件</span>
      </div>

      {sorted.length === 0 ? (
        <div className="progress-empty">
          <strong>この期間の運動記録はありません</strong>
          <p>記録がある日だけ、ここに表示されます。</p>
        </div>
      ) : (
        <div className="activity-list">
          {sorted.map((item) => (
            <article key={item.id}>
              <div className="activity-main">
                <div className="activity-meta">
                  <time>{formatDate(item.occurred_at)}</time>
                  <span>{sourceLabel(item.source)}</span>
                  {item.activity_type && item.activity_type !== "other" && <span>{item.activity_type}</span>}
                </div>
                <strong>{item.title}</strong>
                <div className="activity-details">
                  <span>{item.duration_minutes === null ? "時間 —" : `${Math.round(Number(item.duration_minutes))}分`}</span>
                  <span>{item.calories_burned_kcal === null ? "消費 —" : `${Math.round(Number(item.calories_burned_kcal))}kcal`}</span>
                  <span>{intensityLabel(item.intensity)}</span>
                </div>
                {item.notes && <p>{item.notes}</p>}
              </div>
              <button
                type="button"
                className="activity-delete"
                onClick={() => void deleteActivity(item)}
                disabled={deletingId === item.id}
              >
                {deletingId === item.id ? "削除中…" : "削除"}
              </button>
            </article>
          ))}
        </div>
      )}
    </section>
  );
}
